/**
 * Utility functions for line element coordinate conversions and calculations.
 * Lines use absolute pixel coordinates within the design canvas.
 */

import { rotatePoint } from "lib/geometry/rotation";

/**
 * Calculate the bounding box for a line based on its endpoints.
 * @param {number} x1 - Start point X coordinate (in pixels)
 * @param {number} y1 - Start point Y coordinate (in pixels)
 * @param {number} x2 - End point X coordinate (in pixels)
 * @param {number} y2 - End point Y coordinate (in pixels)
 * @param {number} [padding=0] - Extra padding around the bounding box (for stroke width)
 * @returns {{ top: number, left: number, width: number, height: number }} Bounding box dimensions
 */
export const getLineBoundingBox = (x1, y1, x2, y2, padding = 0) => {
  const minX = Math.min(x1, x2);
  const minY = Math.min(y1, y2);
  const maxX = Math.max(x1, x2);
  const maxY = Math.max(y1, y2);

  // Ensure minimum dimensions for thin/straight lines (for selectability)
  const MIN_DIMENSION = 1;

  const rawWidth = maxX - minX;
  const rawHeight = maxY - minY;

  const width = Math.max(rawWidth, MIN_DIMENSION) + padding * 2;
  const height = Math.max(rawHeight, MIN_DIMENSION) + padding * 2;

  return {
    top: minY - padding,
    left: minX - padding,
    width,
    height
  };
};

/**
 * Calculate the center point of a line.
 * @param {number} x1 - Start point X coordinate
 * @param {number} y1 - Start point Y coordinate
 * @param {number} x2 - End point X coordinate
 * @param {number} y2 - End point Y coordinate
 * @returns {{ x: number, y: number }} Center point coordinates
 */
export const calculateLineCenter = (x1, y1, x2, y2) => ({
  x: (x1 + x2) / 2,
  y: (y1 + y2) / 2
});

/**
 * Calculate the length of a line.
 * @param {number} x1 - Start point X coordinate
 * @param {number} y1 - Start point Y coordinate
 * @param {number} x2 - End point X coordinate
 * @param {number} y2 - End point Y coordinate
 * @returns {number} Length of the line in pixels
 */
export const calculateLineLength = (x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Calculate the angle of a line in degrees.
 * Returns angle from 0-360 where 0 is pointing right (positive X direction).
 * @param {number} x1 - Start point X coordinate
 * @param {number} y1 - Start point Y coordinate
 * @param {number} x2 - End point X coordinate
 * @param {number} y2 - End point Y coordinate
 * @returns {number} Angle in degrees (0-360)
 */
export const getLineAngle = (x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  let angle = Math.atan2(dy, dx) * (180 / Math.PI);
  // Normalize to 0-360 range
  if (angle < 0) {
    angle += 360;
  }
  return angle;
};

/**
 * Standard angle increments for Shift-key snapping (every 45 degrees).
 */
export const ANGLE_INCREMENTS = [0, 45, 90, 135, 180, 225, 270, 315, 360];

/**
 * Constrain a line endpoint to the nearest angle increment.
 * Used when Shift key is held during endpoint drag.
 * @param {number} x1 - Fixed pivot point X coordinate
 * @param {number} y1 - Fixed pivot point Y coordinate
 * @param {number} x2 - Dragged endpoint X coordinate
 * @param {number} y2 - Dragged endpoint Y coordinate
 * @param {number[]} [targetAngles=ANGLE_INCREMENTS] - Angles to snap to
 * @returns {{ x: number, y: number }} Constrained endpoint position
 */
export const constrainToAngle = (
  x1,
  y1,
  x2,
  y2,
  targetAngles = ANGLE_INCREMENTS
) => {
  const currentAngle = getLineAngle(x1, y1, x2, y2);
  const length = calculateLineLength(x1, y1, x2, y2);

  // Find the nearest target angle
  let nearestAngle = targetAngles[0];
  let minDiff = Math.abs(currentAngle - nearestAngle);

  for (const angle of targetAngles) {
    // Handle wrap-around at 360 degrees
    let diff = Math.abs(currentAngle - angle);
    if (diff > 180) {
      diff = 360 - diff;
    }
    if (diff < minDiff) {
      minDiff = diff;
      nearestAngle = angle;
    }
  }

  // Handle 360 same as 0
  if (nearestAngle === 360) {
    nearestAngle = 0;
  }

  // Calculate new endpoint position at the constrained angle
  const angleRad = nearestAngle * (Math.PI / 180);
  return {
    x: x1 + length * Math.cos(angleRad),
    y: y1 + length * Math.sin(angleRad)
  };
};

/**
 * Convert line coordinates to local SVG coordinates.
 * When the SVG is sized to the bounding box, we need to translate
 * the absolute design coordinates to local SVG space.
 * @param {number} x1 - Start point X in design space
 * @param {number} y1 - Start point Y in design space
 * @param {number} x2 - End point X in design space
 * @param {number} y2 - End point Y in design space
 * @param {number} padding - Padding around the bounding box
 * @returns {{ localX1: number, localY1: number, localX2: number, localY2: number }} Local SVG coordinates
 */
export const toLocalSvgCoordinates = (x1, y1, x2, y2, padding = 0) => {
  const minX = Math.min(x1, x2);
  const minY = Math.min(y1, y2);

  return {
    localX1: x1 - minX + padding,
    localY1: y1 - minY + padding,
    localX2: x2 - minX + padding,
    localY2: y2 - minY + padding
  };
};

/**
 * Move a line by a delta, updating both endpoints.
 * @param {{ x1: number, y1: number, x2: number, y2: number }} coordinates - Current line coordinates
 * @param {number} deltaX - Horizontal movement
 * @param {number} deltaY - Vertical movement
 * @returns {{ x1: number, y1: number, x2: number, y2: number }} Updated coordinates
 */
export const moveLineCoordinates = (coordinates, deltaX, deltaY) => ({
  x1: coordinates.x1 + deltaX,
  y1: coordinates.y1 + deltaY,
  x2: coordinates.x2 + deltaX,
  y2: coordinates.y2 + deltaY
});

/**
 * Resize a line by moving one endpoint while keeping the other fixed.
 * @param {{ x1: number, y1: number, x2: number, y2: number }} coordinates - Current line coordinates
 * @param {'start' | 'end'} endpoint - Which endpoint to move
 * @param {number} newX - New X position for the endpoint
 * @param {number} newY - New Y position for the endpoint
 * @returns {{ x1: number, y1: number, x2: number, y2: number }} Updated coordinates
 */
export const resizeLineByEndpoint = (coordinates, endpoint, newX, newY) => {
  if (endpoint === "start") {
    return {
      x1: newX,
      y1: newY,
      x2: coordinates.x2,
      y2: coordinates.y2
    };
  }
  return {
    x1: coordinates.x1,
    y1: coordinates.y1,
    x2: newX,
    y2: newY
  };
};

/**
 * Calculate padding for the line bounding box, including endcap space if needed.
 * @param {number} thickness - Line thickness
 * @param {object} endcap - Endcap configuration
 * @returns {number} Total padding
 */
export const calculateLinePadding = (thickness, endcap) => {
  const hasEndcaps = endcap?.start !== "none" || endcap?.end !== "none";
  const endcapPadding = hasEndcaps ? thickness * 2 : 0;
  return Math.max(thickness / 2, 1) + endcapPadding;
};

/**
 * Rotate both endpoints of a line around a pivot point.
 * Used for group rotation operations where the line must rotate around
 * the selection center rather than its own center.
 * @param {{ x1: number, y1: number, x2: number, y2: number }} coordinates - Current line coordinates
 * @param {number} pivotX - X coordinate of the pivot point
 * @param {number} pivotY - Y coordinate of the pivot point
 * @param {number} angleDegrees - Rotation angle in degrees
 * @returns {{ x1: number, y1: number, x2: number, y2: number }} Rotated coordinates
 */
export const rotateLineCoordinates = (
  coordinates,
  pivotX,
  pivotY,
  angleDegrees
) => {
  const { x1, y1, x2, y2 } = coordinates;

  const newStart = rotatePoint(x1, y1, pivotX, pivotY, angleDegrees);
  const newEnd = rotatePoint(x2, y2, pivotX, pivotY, angleDegrees);

  return {
    x1: newStart.x,
    y1: newStart.y,
    x2: newEnd.x,
    y2: newEnd.y
  };
};
